import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { ExcelService } from '../../services/excel.service';
import { CompanyClients } from 'src/app/shared/model/companyClients';
import { TravelBoardService } from '../../services/travel-board.service';
import { PackLimitationsService } from 'src/app/shared/services/pack-limitations.service';
import { LockedButtonComponent } from 'src/app/shared/components/locked-button/locked-button.component';
import { LimitedFeatures } from 'src/app/shared/models/pack.models';

@Component({
  selector: 'app-export-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslocoModule,
    DialogModule,
    SelectModule,
    ButtonModule,
    LockedButtonComponent,
  ],
  templateUrl: './export-modal.component.html',
})
export class ExportModalComponent implements OnInit {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  private excelService = inject(ExcelService);
  private translocoService = inject(TranslocoService);
  private travelBoardService = inject(TravelBoardService);
  protected packLimitationService = inject(PackLimitationsService);

  years: { value: number; label: string }[] = [];
  clients: { value: string; label: string }[] = [];
  selectedYear?: number;
  selectedClient?: string;
  isExporting = false;
  limitedFeature = LimitedFeatures.Client;

  get tripCount(): number {
    const allTrips = this.travelBoardService.data();
    // Filter only non-draft trips
    let filteredTrips = allTrips.filter((trip) => !trip.isDraft);

    // Filter by year if selected
    if (this.selectedYear && this.selectedYear !== 0) {
      filteredTrips = filteredTrips.filter((trip) => {
        if (!trip.createdAt) return false;
        const tripYear = new Date(trip.createdAt).getFullYear();
        return tripYear === this.selectedYear;
      });
    }

    // Filter by client if selected
    if (this.selectedClient && this.selectedClient !== '') {
      filteredTrips = filteredTrips.filter((trip) =>
        trip.companyClients?.some((client) => client === this.selectedClient),
      );
    }

    return filteredTrips.length;
  }

  ngOnInit() {
    this.loadYears();
    this.loadClients();
  }

  private loadYears() {
    this.excelService.getYears().subscribe({
      next: (years) => {
        const options = years.map((year) => ({
          value: year,
          label: year.toString(),
        }));

        this.translocoService
          .selectTranslate('export.allTrips')
          .subscribe((translation) => {
            options.unshift({ value: 0, label: translation });
            this.years = options;
            this.selectedYear = 0;
          });
      },
    });
  }

  private loadClients() {
    this.excelService.getClients().subscribe({
      next: (clients: CompanyClients[]) => {
        const options = clients.map((client) => ({
          value: client.id,
          label: client.name,
        }));

        this.translocoService
          .selectTranslate('export.allClients')
          .subscribe((translation) => {
            options.unshift({ value: '', label: translation });
            this.clients = options;
            this.selectedClient = '';
          });
      },
    });
  }

  exportExcel() {
    this.isExporting = true;
    this.excelService.export(this.selectedYear, this.selectedClient).subscribe({
      next: (response) => {
        if (!response.body) {
          console.error('Empty file or invalid response');
          this.isExporting = false;
          return;
        }

        const blob = new Blob([response.body], {
          type:
            response.body.type ||
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        const contentDisposition = response.headers.get('Content-Disposition');
        const match = contentDisposition?.match(/filename="(.+)"/);
        const fileName = match ? match[1] : 'greentripper-travels-data.xlsx';

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);

        this.isExporting = false;
        this.onClose();
      },
      error: () => {
        this.isExporting = false;
      },
    });
  }

  onClose() {
    this.visibleChange.emit(false);
  }
}
